/**
 * API Client for AI Try-On
 */

// Production serves the API through the same custom domain route.
const API_BASE = import.meta.env.PROD ? '/api' : import.meta.env.VITE_API_URL || '/api'

export interface PublicConfigResponse {
  liffRichmenuAppId?: string
  liffMirrorAppId?: string
  appUrl?: string
}

export interface CheckoutSessionResponse {
  url?: string
  error?: string
}

export interface SubscriptionCheckResponse {
  hasEver?: boolean
  active?: boolean
}

export interface RichmenuLinkResponse {
  url?: string
  error?: string
}

export async function fetchPublicConfig(): Promise<PublicConfigResponse> {
  const res = await fetch(`${API_BASE}/public-config`)
  if (!res.ok) return {}
  const data = (await res.json()) as Record<string, unknown>
  return {
    liffRichmenuAppId: typeof data.liffRichmenuAppId === 'string' ? data.liffRichmenuAppId : undefined,
    liffMirrorAppId: typeof data.liffMirrorAppId === 'string' ? data.liffMirrorAppId : undefined,
    appUrl: typeof data.appUrl === 'string' ? data.appUrl : undefined,
  }
}

export async function createCheckoutSession(
  lineUserId: string,
  plan: 'ticket10' | 'standard' | 'premium'
): Promise<CheckoutSessionResponse> {
  const res = await fetch(`${API_BASE}/stripe/create-checkout-session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lineUserId, plan }),
  })
  const data = (await res.json()) as Record<string, unknown>
  return {
    url: typeof data.url === 'string' ? data.url : undefined,
    error: typeof data.error === 'string' ? data.error : undefined,
  }
}

export async function checkSubscription(lineUserId: string): Promise<SubscriptionCheckResponse> {
  const res = await fetch(`${API_BASE}/subscription/check`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ lineUserId }),
  })
  if (!res.ok) return {}
  const data = (await res.json()) as Record<string, unknown>
  return {
    hasEver: typeof data.hasEver === 'boolean' ? data.hasEver : undefined,
    active: typeof data.active === 'boolean' ? data.active : undefined,
  }
}

export async function getRichmenuLink(
  target: string,
  lineUserId?: string
): Promise<RichmenuLinkResponse> {
  const res = await fetch(`${API_BASE}/line/richmenu-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ target, lineUserId }),
  })
  const data = (await res.json()) as Record<string, unknown>
  return {
    url: typeof data.url === 'string' ? data.url : undefined,
    error: typeof data.error === 'string' ? data.error : undefined,
  }
}

export interface MirrorStatus {
  remaining: number | null
  unlimited: boolean
  maxSeconds: number
}

export interface MirrorSessionResult {
  token: string
  model: string
  maxSeconds: number
  remaining: number | null
  unlimited: boolean
}

export class MirrorApiError extends Error {
  constructor(
    public kind: 'unauthorized' | 'unavailable' | 'no_credit' | 'decart_failed' | 'unknown',
    message: string
  ) {
    super(message)
  }
}

function parseMirrorStatus(data: Record<string, unknown>): MirrorStatus {
  return {
    remaining: typeof data.remaining === 'number' ? data.remaining : null,
    unlimited: data.unlimited === true,
    maxSeconds: typeof data.maxSeconds === 'number' ? data.maxSeconds : 60,
  }
}

export async function fetchMirrorStatus(idToken: string): Promise<MirrorStatus> {
  const res = await fetch(`${API_BASE}/mirror/status`, {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  if (res.status === 401) throw new MirrorApiError('unauthorized', 'unauthorized')
  if (res.status === 503) throw new MirrorApiError('unavailable', 'unavailable')
  if (!res.ok) throw new MirrorApiError('unknown', `status ${res.status}`)
  return parseMirrorStatus((await res.json()) as Record<string, unknown>)
}

export async function createMirrorSession(idToken: string): Promise<MirrorSessionResult> {
  const res = await fetch(`${API_BASE}/mirror/session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  })
  if (res.status === 401) throw new MirrorApiError('unauthorized', 'unauthorized')
  if (res.status === 503) throw new MirrorApiError('unavailable', 'unavailable')
  if (res.status === 402) throw new MirrorApiError('no_credit', 'no_mirror_credit')
  if (res.status === 502) throw new MirrorApiError('decart_failed', 'decart_failed')
  if (!res.ok) throw new MirrorApiError('unknown', `status ${res.status}`)
  const data = (await res.json()) as Record<string, unknown>
  if (typeof data.token !== 'string') throw new MirrorApiError('unknown', 'invalid response')
  return {
    token: data.token,
    model: typeof data.model === 'string' ? data.model : 'lucy-vton-latest',
    ...parseMirrorStatus(data),
  }
}
